import { Calendar, ArrowRight, Scissors, Clock, BadgeCheck, User, Star } from "lucide-react";
import { useInViewRef } from "./shared";
import portrait from "@/assets/stache-barbers-portrait.jpg";
import logo from "@/assets/stache-barbers-logo.webp";


const STAGGER = 90;

export function StacheBarbersVisual() {
  const { ref, inView } = useInViewRef<HTMLDivElement>();

  const services = [
    { icon: Scissors, title: "Classic Cuts", sub: "Clean. Timeless. You." },
    { icon: Calendar, title: "Straight Razor", sub: "Smooth. Sharp. Sophisticated." },
    { icon: User, title: "Hot Towel Shave", sub: "Relax. Refresh. Refined." },
    { icon: Scissors, title: "Fade & Style", sub: "Sharp fades. Fresh finishes." },
  ];

  const trust = [
    { icon: Clock, label: "Top Barber Shop" },
    { icon: BadgeCheck, label: "Premium Products" },
    { icon: User, label: "Experienced Barbers" },
    { icon: Star, label: "Satisfaction Guaranteed" },
  ];

  const enter = (i: number) =>
    `transition-all duration-700 ease-out ${
      inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
    }`;

  // Floating service icons drifting in the hero background
  const floatingServices = [
    { Icon: Scissors, top: "8%", left: "6%", delay: "0s", duration: "6s", size: "h-5 w-5" },
    { Icon: Calendar, top: "18%", right: "8%", delay: "1.2s", duration: "7s", size: "h-4 w-4" },
    { Icon: User, bottom: "22%", left: "10%", delay: "2.4s", duration: "6.5s", size: "h-4 w-4" },
    { Icon: Star, top: "44%", left: "2%", delay: "0.6s", duration: "7.5s", size: "h-3.5 w-3.5" },
    { Icon: BadgeCheck, bottom: "10%", right: "6%", delay: "1.8s", duration: "6.8s", size: "h-4 w-4" },
    { Icon: Scissors, top: "30%", right: "16%", delay: "3s", duration: "7.2s", size: "h-3 w-3" },
  ];

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-xl bg-white p-4 sm:p-5"
    >
      {/* Top nav */}
      <div
        className={enter(0)}
        style={{ transitionDelay: `${0 * STAGGER}ms` }}
      >
        <div className="flex items-center justify-between">
          {/* Stache Barbers logo */}
          <img
            src={logo}
            alt="Stache Barbers"
            className="h-10 w-auto sm:h-12"
          />

          <div className="hidden items-center gap-4 text-[10px] font-medium text-foreground/80 md:flex lg:gap-6 lg:text-xs">
            <span>About</span>
            <span>Services</span>
            <span>Gallery</span>
            <span>Reviews</span>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 px-3 py-1.5 text-[10px] font-semibold text-white shadow-md sm:px-4 sm:py-2 sm:text-xs">
            Book Now
            <Calendar className="h-3 w-3" />
          </div>
        </div>
      </div>


      {/* Hero */}
      <div className="mt-4 grid grid-cols-2 items-start gap-3 sm:gap-4">
        {/* Hero image (animated portrait + glow + floating service icons) */}
        <div
          className={`relative aspect-[5/4] ${enter(2)}`}
          style={{ transitionDelay: `${2 * STAGGER}ms` }}
        >
          {/* Glow */}
          <div
            className={`absolute -inset-2 rounded-2xl bg-gradient-to-br from-violet-500/40 via-indigo-500/30 to-fuchsia-500/40 blur-2xl transition-opacity duration-1000 ${
              inView ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDelay: "400ms" }}
            aria-hidden="true"
          />

          {/* Floating service icons drifting in the background */}
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute -inset-4 z-20 transition-opacity duration-1000 ${
              inView ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDelay: "600ms" }}
          >
            {floatingServices.map((f, i) => {
              const Icon = f.Icon;
              return (
                <Icon
                  key={i}
                  className={`absolute ${f.size} text-indigo-500/60 drop-shadow-[0_0_6px_rgba(139,92,246,0.5)]`}
                  style={{
                    top: f.top,
                    left: f.left,
                    right: f.right,
                    bottom: f.bottom,
                    animation: `stache-float ${f.duration} ease-in-out ${f.delay} infinite`,
                  }}
                  strokeWidth={2}
                />
              );
            })}
            <style>{`
              @keyframes stache-float {
                0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.55; }
                50% { transform: translateY(-8px) rotate(6deg); opacity: 0.9; }
              }
            `}</style>
          </div>

          <div className="relative h-full w-full overflow-hidden rounded-xl border border-border/60 shadow-xl">
            <img
              src={portrait}
              alt="Stylish man with modern pompadour and beard at Stache Barbers"
              loading="lazy"
              className={`h-full w-full -scale-x-100 object-cover transition-transform duration-[1400ms] ease-out ${
                inView ? "scale-y-100" : "scale-y-110"
              }`}
            />
            {/* Subtle purple color wash to match brand */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-fuchsia-500/15 mix-blend-overlay" />
          </div>
        </div>

        {/* Headline + CTA */}
        <div
          className={enter(1)}
          style={{ transitionDelay: `${1 * STAGGER}ms` }}
        >
          <h3 className="text-lg font-extrabold leading-tight tracking-tight text-foreground sm:text-2xl lg:text-[28px]">
            Sharp style.
            <br />
            <span className="bg-gradient-to-r from-indigo-500 to-violet-600 bg-clip-text text-transparent">
              Confident
            </span>{" "}
            you.
          </h3>
          <p className="mt-2 text-[10px] leading-snug text-foreground/70 sm:text-xs">
            Modern cuts and grooming
            <br />
            for today's man.
          </p>

          <div
            className={`mt-3 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 px-3 py-2 text-[10px] font-semibold text-white shadow-lg sm:mt-4 sm:px-4 sm:py-2.5 sm:text-xs ${enter(2)}`}
            style={{ transitionDelay: `${2 * STAGGER}ms` }}
          >
            <Calendar className="h-3 w-3" />
            <span className="sm:hidden">Book Now</span>
            <span className="hidden sm:inline">Book Appointment</span>
            <ArrowRight className="h-3 w-3" />
          </div>

          <p className="mt-2 text-[9px] text-foreground/70 sm:text-[10px]">Walk-ins welcome</p>
        </div>
      </div>


      {/* Our Services */}
      <div className="mt-4 sm:mt-5">
        <div
          className={`mb-2 flex items-center gap-2 ${enter(3)}`}
          style={{ transitionDelay: `${3 * STAGGER}ms` }}
        >
          <div className="h-px flex-1 bg-foreground/15" />
          <span className="text-[10px] font-bold text-foreground sm:text-xs">Our Services</span>
          <div className="h-px flex-1 bg-foreground/15" />
        </div>

        <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className={`rounded-lg border border-border/60 bg-card/40 p-1.5 text-center sm:p-2 ${enter(4 + i)}`}
                style={{ transitionDelay: `${(4 + i) * STAGGER}ms` }}
              >
                <Icon className="mx-auto h-4 w-4 text-indigo-500 sm:h-5 sm:w-5" strokeWidth={2.25} />
                <p className="mt-1 text-[8px] font-bold text-foreground sm:text-[10px]">
                  {s.title}
                </p>
                <p className="mt-0.5 text-[7px] leading-tight text-foreground/60 sm:text-[8px]">
                  {s.sub}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust bar */}
      <div
        className={`mt-3 border-t border-border/60 pt-2 sm:mt-4 sm:pt-3 ${enter(8)}`}
        style={{ transitionDelay: `${8 * STAGGER}ms` }}
      >
        <div className="grid grid-cols-4 gap-1">
          {trust.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className="flex items-center justify-center gap-1">
                <Icon className="h-2.5 w-2.5 text-indigo-500 sm:h-3 sm:w-3" strokeWidth={2.25} />
                <span className="text-[7px] font-medium text-foreground/80 sm:text-[9px]">
                  {t.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
